import { Component, ElementRef, Inject, ViewChild } from "@angular/core";
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Observable } from "rxjs";
import { AdmLayoutEx } from "../../api/models/adm-layout-ex";
import { AdmCatalogTable } from "../../api/models/adm-catalog-table";
import { AdmCatalogColumn } from "../../api/models/adm-catalog-column";
import { AdmGroupLite } from "../../api/models/adm-group-lite";

export interface AdmColumnGroup {
  admFieldGroupId: number | null;
  name: string;
  columns: AdmCatalogColumn[];
}
import { AdmSubscriptionLite } from "../../api/models/adm-subscription-lite";
import { US_PreFilter } from "../../api/models/us_pre-filter";
import { AdmLayoutService } from "../../api/services/adm-layout.service";
import { PreFilterService } from "../../api/services/pre-filter.service";
import { FilterEditorDialogComponent, FilterEditorDialogResult } from "../filter-editor-dialog/filter-editor-dialog.component";
import { ConfirmDialogComponent } from "../confirm-dialog/confirm-dialog.component";
import { DialogResult, DialogResultType } from "../DialogResult";

export interface AdmLayoutDialogData {
  layout: AdmLayoutEx;
  subscriptions: AdmSubscriptionLite[];
  preFilters: US_PreFilter[];
  initialErrorMessage?: string;
  isAdmin: boolean;
}

@Component({
  selector: "adm-layout-dialog",
  templateUrl: "./adm-layout-dialog.component.html",
  styleUrls: ["./adm-layout-dialog.component.css"],
})
export class AdmLayoutDialogComponent {
  @ViewChild("fileInput") fileInput: ElementRef<HTMLInputElement>;

  public result: AdmLayoutEx;
  public filteringSource: number = -1;
  public selectedColumnIds: Set<number> = new Set();
  // Every ADM Group this subscription's plan type grants - a layout is ISOLATED to exactly one (never a union),
  // so the picker only shows a "Group" dropdown once there is more than one to choose from, auto-selecting the
  // only one otherwise (see AdmLayoutController.GetAvailableGroups).
  public availableGroups: AdmGroupLite[] = [];
  public loadingGroups = false;
  // Only tables/columns the SELECTED Group offers - the server enforces it again on save.
  public catalog: AdmCatalogTable[] = [];
  public selectedTableId: number | null = null;
  public loadingCatalog = false;
  public errorMessage: string = null;
  // Which FilterEditorDialogComponent tabs are relevant to this subscription's catalog (e.g. "SourceJobPosting"
  // only once a Source Job Posting filter point targets a column on one of its tables) - see
  // AdmLayoutController.GetAvailableFilterCategories.
  public availableFilterKeys: string[] = [];
  public uploadStatusMessage: string = null;
  public uploadErrorMessage: string = null;

  public Result: DialogResult = { ResultType: DialogResultType.Cancel, ResultData: null };

  constructor(
    public dialogRef: MatDialogRef<AdmLayoutDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AdmLayoutDialogData,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private admLayoutService: AdmLayoutService,
    private preFilterService: PreFilterService
  ) {
    this.result = { ...data.layout };
    this.filteringSource = this.result.preFilterId ?? -1;
    this.selectedColumnIds = new Set(this.result.selectedColumnIds || []);
    this.errorMessage = data.initialErrorMessage || null;
    this.loadGroups();
  }

  get isNew(): boolean {
    return !this.data.layout.admLayoutId;
  }

  get selectedTable(): AdmCatalogTable | null {
    return this.catalog.find((x) => x.admTableId === this.selectedTableId) || this.catalog[0] || null;
  }

  loadGroups() {
    if (!this.result.subscriptionId) return;
    this.loadingGroups = true;
    this.admLayoutService.admLayoutGetAvailableGroups(this.result.subscriptionId).subscribe({
      next: (groups) => {
        this.availableGroups = groups || [];
        this.loadingGroups = false;
        // A new layout with exactly one Group available skips the dropdown entirely and just uses it; an
        // existing layout keeps whatever Group it was built from (set once, never changed - see onSave).
        if (this.isNew && !this.result.admGroupId && this.availableGroups.length === 1)
          this.result.admGroupId = this.availableGroups[0].admGroupId;
        this.loadCatalog();
      },
      error: () => {
        this.availableGroups = [];
        this.loadingGroups = false;
        this.errorMessage = "Could not load the Agent Data Model Groups available to this subscription.";
      },
    });
  }

  loadCatalog() {
    if (!this.result.subscriptionId || !this.result.admGroupId) {
      this.catalog = [];
      this.selectedTableId = null;
      this.availableFilterKeys = [];
      return;
    }
    this.loadingCatalog = true;
    this.admLayoutService.admLayoutGetCatalog({ subscriptionId: this.result.subscriptionId, groupId: this.result.admGroupId }).subscribe({
      next: (catalog) => {
        this.catalog = catalog || [];
        this.selectedTableId = this.catalog.length > 0 ? this.catalog[0].admTableId : null;
        this.loadingCatalog = false;
      },
      error: () => {
        this.catalog = [];
        this.loadingCatalog = false;
        this.errorMessage = "Could not load the tables available to this Group.";
      },
    });
    this.admLayoutService.admLayoutGetAvailableFilterCategories({ subscriptionId: this.result.subscriptionId, groupId: this.result.admGroupId }).subscribe({
      next: (categories) => (this.availableFilterKeys = categories || []),
      error: () => (this.availableFilterKeys = []),
    });
  }

  onSubscriptionChange() {
    this.selectedColumnIds.clear();
    this.result.admGroupId = null;
    this.loadGroups();
  }

  onGroupChange() {
    this.selectedColumnIds.clear();
    this.loadCatalog();
  }

  columns(table: AdmCatalogTable | null): AdmCatalogColumn[] {
    return (table && table.columns) || [];
  }

  // The selected table's columns bucketed under their field groups (server order), with any ungrouped columns in a
  // trailing "General" bucket - only shown if there is something in it. Field groups with no currently-entitled
  // columns are skipped: nothing in them to select.
  columnGroups(table: AdmCatalogTable | null): AdmColumnGroup[] {
    const cols = this.columns(table);
    const groups = ((table && table.fieldGroups) || []).map((g) => ({
      admFieldGroupId: g.admFieldGroupId,
      name: g.name,
      columns: cols.filter((c) => c.admFieldGroupId === g.admFieldGroupId),
    })).filter((g) => g.columns.length > 0);

    const ungrouped = cols.filter((c) => c.admFieldGroupId == null);
    if (ungrouped.length > 0) groups.push({ admFieldGroupId: null, name: "General", columns: ungrouped });
    return groups;
  }

  // Called directly from *ngFor, so it returns new wrapper objects every change-detection run. Without trackBy,
  // that makes *ngFor's default identity diffing tear down and rebuild the checkboxes on every tick - a click's
  // mousedown can land on a checkbox CD has already replaced by the time the click completes, so it silently does
  // nothing (see the identical bug/fix in admin-adm-definition.component.ts). Track by the stable field group id.
  trackByFieldGroupId(_index: number, group: AdmColumnGroup): number | null {
    return group.admFieldGroupId;
  }

  trackByColumnId(_index: number, c: AdmCatalogColumn): number {
    return c.admTableColumnId;
  }

  isSelected(column: AdmCatalogColumn): boolean {
    return this.selectedColumnIds.has(column.admTableColumnId);
  }

  toggleColumn(column: AdmCatalogColumn, checked: boolean) {
    if (checked) this.selectedColumnIds.add(column.admTableColumnId);
    else this.selectedColumnIds.delete(column.admTableColumnId);
  }

  selectedCount(table: AdmCatalogTable): number {
    return this.columns(table).filter((c) => this.isSelected(c)).length;
  }

  isTableSelected(table: AdmCatalogTable): boolean {
    const cols = this.columns(table);
    return cols.length > 0 && cols.every((c) => this.isSelected(c));
  }

  isTableIndeterminate(table: AdmCatalogTable): boolean {
    const n = this.selectedCount(table);
    return n > 0 && n < this.columns(table).length;
  }

  toggleTable(table: AdmCatalogTable, checked: boolean) {
    this.columns(table).forEach((c) => this.toggleColumn(c, checked));
  }

  isGroupSelected(group: AdmColumnGroup): boolean {
    return group.columns.length > 0 && group.columns.every((c) => this.isSelected(c));
  }

  isGroupIndeterminate(group: AdmColumnGroup): boolean {
    const n = group.columns.filter((c) => this.isSelected(c)).length;
    return n > 0 && n < group.columns.length;
  }

  toggleGroup(group: AdmColumnGroup, checked: boolean) {
    group.columns.forEach((c) => this.toggleColumn(c, checked));
  }

  editDirectFilter(): boolean {
    FilterEditorDialogComponent.showDialog(this.dialog, this.result.directFilterData, [], undefined, [], this.availableFilterKeys).subscribe((result) => {
      if (result.ResultType === DialogResultType.Ok) {
        const results: FilterEditorDialogResult = result.ResultData;
        this.result.directFilterData = results.filterData;
      }
    });
    return false;
  }

  // Same "Upload Filter Template" mechanism as DoD's PreFilters (pre-filter-editor-dialog.component.ts): parses
  // an uploaded spreadsheet into a filter and replaces the whole Direct Filter with it, after confirming when
  // there's already a non-empty one.
  handleFileInput(files: FileList): void {
    if (this.isFilterEmpty(this.result.directFilterData)) {
      this.processTemplate(files);
    } else {
      ConfirmDialogComponent.showDialog(this.dialog, "Overwrite Filter?", "Are you sure you want to overwrite the current filter?").subscribe((r) => {
        if (r.ResultType === DialogResultType.Ok) this.processTemplate(files);
        else if (this.fileInput?.nativeElement) this.fileInput.nativeElement.value = "";
      });
    }
  }

  private processTemplate(files: FileList): void {
    this.uploadErrorMessage = null;
    this.uploadStatusMessage = null;
    const uploadFile = files.item(0);
    if (!uploadFile) return;
    if (uploadFile.size > 10000000) {
      this.uploadErrorMessage = "File size is too large. Max file size is 10MB.";
      if (this.fileInput?.nativeElement) this.fileInput.nativeElement.value = "";
      return;
    }
    this.uploadStatusMessage = `Processing ${uploadFile.name}, please wait...`;
    this.preFilterService.preFilterProcessPreFilterTemplate(uploadFile).subscribe({
      next: (result) => {
        if (result.hasError) {
          this.uploadErrorMessage = result.errorMessage;
          this.uploadStatusMessage = null;
        } else {
          this.result.directFilterData = result.filter;
          this.filteringSource = -1;
          this.uploadStatusMessage = "Template applied.";
        }
        if (this.fileInput?.nativeElement) this.fileInput.nativeElement.value = "";
      },
      error: (err) => {
        this.uploadErrorMessage = "Upload failed: " + err;
        this.uploadStatusMessage = null;
        if (this.fileInput?.nativeElement) this.fileInput.nativeElement.value = "";
      },
    });
  }

  copyPreFilterToDirect(): void {
    const preFilter = this.data.preFilters.find((x) => x.preFilterId === this.filteringSource);
    if (!preFilter) return;

    const performCopy = () => {
      this.result.directFilterData = preFilter.filterData;
      this.filteringSource = -1;
      this.snackBar.open("PreFilter copied to Direct Filter", null, { duration: 5000 });
    };

    if (!this.result.directFilterData || this.result.directFilterData === "{}") {
      performCopy();
    } else {
      ConfirmDialogComponent.showDialog(this.dialog, "Overwrite Filter?", "Are you sure you want to overwrite the current filter?").subscribe((r) => {
        if (r.ResultType === DialogResultType.Ok) performCopy();
      });
    }
  }

  // Same definition of "empty" as the server (AdmFilterEmptiness): Last Month with nothing else selected.
  private isFilterEmpty(filterData: string | undefined): boolean {
    if (!filterData || filterData === "{}") return true;
    try {
      const keys = JSON.parse(filterData);
      return keys.dateRangeType === 1 &&
        keys.dateRange === 2 &&
        (!keys.titles || keys.titles.length === 0) &&
        (!keys.occupations || keys.occupations.length === 0) &&
        (!keys.companies || keys.companies.length === 0) &&
        (!keys.industries || keys.industries.length === 0) &&
        (!keys.locations || keys.locations.length === 0) &&
        (!keys.metroGroups || keys.metroGroups.length === 0) &&
        (!keys.attributes || keys.attributes.length === 0) &&
        (!keys.educationInstitutions || keys.educationInstitutions.length === 0);
    } catch (e) {
      return false;
    }
  }

  onSave(): void {
    if (!this.result.name || this.result.name.trim() === "") {
      this.errorMessage = "Name is required.";
      return;
    }
    if (!this.result.admGroupId) {
      this.errorMessage = "An Agent Data Model Group is required.";
      return;
    }
    if (this.selectedColumnIds.size === 0) {
      this.errorMessage = "At least one column is required.";
      return;
    }
    // Enforced server-side too: an Agent Data Model must never expose an entire source table.
    if (this.filteringSource === -1 && this.isFilterEmpty(this.result.directFilterData)) {
      this.errorMessage = "Some filtering is required - select a PreFilter or configure a Direct Filter.";
      return;
    }

    this.result.preFilterId = this.filteringSource === -1 ? null : this.filteringSource;
    this.result.selectedColumnIds = Array.from(this.selectedColumnIds);

    this.Result.ResultType = DialogResultType.Ok;
    this.Result.ResultData = this.result;
    this.dialogRef.close(this.Result);
  }

  onCancelClick(): void {
    this.dialogRef.close(this.Result);
  }

  static showDialog(dialog: MatDialog, data: AdmLayoutDialogData): Observable<DialogResult> {
    return dialog.open(AdmLayoutDialogComponent, { disableClose: false, width: "800px", data }).afterClosed();
  }
}
